import { ISSUE_MGMT_WRITE_PERMISSION } from '@cortex/domain-ac-mens-hub-api';
import { ToastProvider } from '@cortex/web-design-system/components/ui/Toast';
import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useTodoCards } from '../../hooks/useIssueTodo.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import { AnomalyBands } from './AnomalyBands.js';

/**
 * タブ1つ分（文言キーと行き先。editOnly は編集キーの人だけに出す）。
 * @graph-connects none
 */
const TABS = [
  { key: 'issues', labelKey: 'issueMgmt.tab.issues', to: '/issue-mgmt/issues', editOnly: false },
  { key: 'todo', labelKey: 'issueMgmt.tab.todo', to: '/issue-mgmt/todo', editOnly: true },
  {
    key: 'settings',
    labelKey: 'issueMgmt.tab.settings',
    to: '/issue-mgmt/settings',
    editOnly: false,
  },
] as const;

/** タブの共通の見た目。 @graph-connects none */
const TAB_CLASS =
  'flex items-center gap-2 border-b-2 px-5 py-2 text-[13px] font-semibold transition-colors -mb-px';

/**
 * 今のパスがどのタブか（課題の詳細は課題のタブ）。
 * @graph-connects none
 */
function currentTab(pathname: string): (typeof TABS)[number]['key'] {
  if (pathname.startsWith('/issue-mgmt/settings')) return 'settings';
  if (pathname.startsWith('/issue-mgmt/todo')) return 'todo';
  return 'issues';
}

/**
 * 課題管理の枠（見出し・タブ・異常の帯・トースト）。タブは「課題・やること・設定」で、最初に開くのは課題。
 * やることは編集キーの人だけに出し、残りのカードの数（期限切れがあれば赤）を添える。
 * 異常の帯（取込みが止まった・Backlog の写しが古い）は課題とやることの上に出す。
 * トーストは枠に置き、課題を作って詳細へ移っても消えないようにする。
 *
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の見出しと「課題・やること・設定」のタブ（やることは編集キーの人だけ・残りの数つき）を出し、異常の帯と、タブをまたいでも消えない保存の知らせ（トースト）の枠を作る
 * @graph-connects ToastProvider [calls] トースト
 * @graph-connects usePermissions [calls] 編集キーの有無
 * @graph-connects useTodoCards [calls] 残りのカードの数
 * @graph-connects AnomalyBands [calls] 異常の帯
 */
export function IssueMgmtLayout() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const current = currentTab(pathname);
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const canEdit = !permissionsLoading && hasPermission(ISSUE_MGMT_WRITE_PERMISSION);
  const todo = useTodoCards(canEdit);
  const cards = todo.data?.cards ?? null;
  const overdue = cards?.some((card) => card.overdue) ?? false;
  return (
    <ToastProvider>
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <h1 className="mb-4 text-2xl font-bold text-[var(--color-text-primary)]">
          {t('issueMgmt.title')}
        </h1>
        <div role="tablist" className="mb-5 flex border-b border-[var(--color-border)]">
          {TABS.filter((tab) => canEdit || !tab.editOnly).map((tab) => (
            <Link
              key={tab.key}
              to={tab.to}
              role="tab"
              aria-selected={current === tab.key}
              className={`${TAB_CLASS} ${
                current === tab.key
                  ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
              }`}
            >
              {t(tab.labelKey)}
              {tab.key === 'todo' && cards !== null && (
                <span
                  className={`rounded-full px-2 text-[11px] leading-5 ${
                    overdue
                      ? 'bg-[var(--color-error)] text-white'
                      : 'bg-[var(--color-bg-muted)] text-[var(--color-text-secondary)]'
                  }`}
                >
                  {cards.length}
                </span>
              )}
            </Link>
          ))}
        </div>
        {current !== 'settings' && <AnomalyBands />}
        <Outlet />
      </div>
    </ToastProvider>
  );
}
